import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import axios from 'axios';
import {
  BookOpen,
  Calendar,
  Users,
  Trophy,
  ChevronRight,
  CheckCircle,
  Star,
  TreePine,
  ArrowLeft,
  Sparkles,
  Loader2,
  PenLine
} from 'lucide-react';
import { toast } from 'sonner';

// Import API URL from utility
import { API_URL } from '../lib/api';

const ReadathonPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  const [readathons, setReadathons] = useState([]);
  const [selectedReadathon, setSelectedReadathon] = useState(null);
  const [myProgress, setMyProgress] = useState({});
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signingUp, setSigningUp] = useState(false);

  useEffect(() => {
    fetchReadathons();
  }, []);

  useEffect(() => {
    if (selectedReadathon && isAuthenticated) {
      fetchMyProgress(selectedReadathon.readathon_id);
      fetchLeaderboard(selectedReadathon.readathon_id);
    }
  }, [selectedReadathon, isAuthenticated]);

  const fetchReadathons = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/readathons`);
      setReadathons(response.data);
      // Select the first active readathon by default
      const activeRa = response.data.find(r => r.is_active);
      if (activeRa) {
        setSelectedReadathon(activeRa);
      } else if (response.data.length > 0) {
        setSelectedReadathon(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching readathons:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyProgress = async (readathonId) => {
    if (!isAuthenticated) return;
    try {
      const response = await axios.get(
        `${API_URL}/api/readathons/${readathonId}/my-progress`,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setMyProgress(prev => ({ ...prev, [readathonId]: response.data }));
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  const fetchLeaderboard = async (readathonId) => {
    try {
      const response = await axios.get(`${API_URL}/api/readathons/${readathonId}/leaderboard`);
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  const handleSignup = async (readathonId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setSigningUp(true);
    try {
      await axios.post(
        `${API_URL}/api/readathons/${readathonId}/signup`,
        {},
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success("You're signed up! Let the reading begin!");
      fetchMyProgress(readathonId);
      fetchReadathons(); // Refresh to update UI
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to sign up';
      toast.error(message);
    } finally {
      setSigningUp(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-sky-50 flex items-center justify-center">
        <div className="text-center">
          <TreePine className="w-16 h-16 text-emerald-500 mx-auto animate-pulse" />
          <p className="mt-4 text-slate-600">Loading readathons...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-sky-50 to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-emerald-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-emerald-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
            
            {isAuthenticated ? (
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-6"
              >
                My Library
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/login')}
                className="bg-coral-500 hover:bg-coral-600 text-white rounded-full px-6"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
            <Trophy className="w-5 h-5 text-amber-600" />
            <span className="font-medium text-amber-700">Bible Readathon</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-slate-800 mb-6">
            Read the Bible <span className="text-emerald-600">Together</span>
          </h1>
          
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
            Join our community reading challenges and journey through God&apos;s Word together. 
            Track your progress, earn achievements, and grow in faith!
          </p>
        </div>

        {/* Active Readathons */}
        {readathons.filter(r => r.is_active).length > 0 && (
          <section className="mb-12">
            <h2 className="text-2xl font-serif font-bold text-slate-800 mb-6 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-emerald-500" />
              Active Readathons - Join Now!
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {readathons.filter(r => r.is_active).map((ra) => {
                const progress = myProgress[ra.readathon_id];
                const isSignedUp = progress?.signed_up;
                
                return (
                  <div 
                    key={ra.readathon_id}
                    className="bg-white rounded-2xl shadow-lg border border-emerald-100 overflow-hidden"
                  >
                    <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-6 text-white">
                      <div className="flex items-center justify-between mb-2">
                        <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                          Active Now
                        </span>
                        {isSignedUp && (
                          <span className="bg-amber-400 text-amber-900 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" /> Joined
                          </span>
                        )}
                      </div>
                      <h3 className="text-2xl font-serif font-bold mb-1">{ra.title}</h3>
                      <p className="text-emerald-100 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {formatDate(ra.start_date)} - {formatDate(ra.end_date)}
                      </p>
                    </div>
                    
                    <div className="p-6">
                      <p className="text-slate-600 mb-4">{ra.description}</p>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        {ra.books?.map((book, idx) => (
                          <span key={idx} className="bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-sm">
                            {book}
                          </span>
                        ))}
                      </div>
                      
                      <div className="flex items-center justify-between text-sm text-slate-500 mb-4">
                        <span className="flex items-center gap-1">
                          <BookOpen className="w-4 h-4" />
                          {ra.total_chapters} chapters
                        </span>
                      </div>
                      
                      {isSignedUp ? (
                        <div className="space-y-3">
                          <div className="bg-slate-50 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-slate-700">Your Progress</span>
                              <span className="text-lg font-bold text-emerald-600">{progress.progress_percent || 0}%</span>
                            </div>
                            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-emerald-500 transition-all"
                                style={{ width: `${progress.progress_percent || 0}%` }}
                              />
                            </div>
                            <p className="text-xs text-slate-500 mt-2">
                              {progress.chapters_read || 0} of {ra.total_chapters} chapters read
                            </p>
                          </div>
                          <Button 
                            onClick={() => navigate(`/readathon/${ra.readathon_id}`)}
                            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                          >
                            <PenLine className="w-4 h-4 mr-2" /> Open Journal & Discussion
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={() => handleSignup(ra.readathon_id)}
                          disabled={signingUp}
                          className="w-full bg-coral-500 hover:bg-coral-600 text-white"
                        >
                          {signingUp ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <ChevronRight className="w-4 h-4 mr-2" />
                          )}
                          {isAuthenticated ? 'Join This Readathon' : 'Sign In to Join'}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Past/Completed Readathons */}
        {readathons.filter(r => !r.is_active).length > 0 && (
          <section className="mb-12">
            <h2 className="text-2xl font-serif font-bold text-slate-800 mb-6 flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-slate-400" />
              Past Readathons
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {readathons.filter(r => !r.is_active).map((ra) => (
                <div 
                  key={ra.readathon_id}
                  className="bg-white rounded-xl shadow border border-slate-100 overflow-hidden opacity-75"
                >
                  <div className="bg-slate-100 p-4">
                    <span className="text-xs font-medium text-slate-500 uppercase">Completed</span>
                    <h3 className="text-lg font-bold text-slate-700 mt-1">{ra.title}</h3>
                    <p className="text-sm text-slate-500">
                      {formatDate(ra.start_date)} - {formatDate(ra.end_date)}
                    </p>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-slate-600 mb-3">{ra.description}</p>
                    <div className="flex flex-wrap gap-1">
                      {ra.books?.slice(0, 3).map((book, idx) => (
                        <span key={idx} className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-xs">
                          {book}
                        </span>
                      ))}
                      {ra.books?.length > 3 && (
                        <span className="text-xs text-slate-400">+{ra.books.length - 3} more</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* No Readathons State */}
        {readathons.length === 0 && (
          <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-8 mb-12 text-center">
            <Trophy className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-2xl font-serif font-bold text-slate-800 mb-2">
              No Readathons Yet
            </h2>
            <p className="text-slate-600 mb-4">
              Check back soon for upcoming Bible reading challenges!
            </p>
          </div>
        )}

        {/* Leaderboard */}
        {selectedReadathon && leaderboard.length > 0 && (
          <section className="mb-12">
            <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-8">
              <div className="flex items-center gap-3 mb-6">
                <Trophy className="w-6 h-6 text-amber-500" />
                <h2 className="text-2xl font-serif font-bold text-slate-800">
                  Leaderboard - {selectedReadathon.title}
                </h2>
              </div>
              
              <div className="space-y-3">
                {leaderboard.slice(0, 10).map((entry, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-4 p-4 rounded-xl ${
                      idx === 0 ? 'bg-amber-50 border-2 border-amber-200' :
                      idx === 1 ? 'bg-slate-50 border border-slate-200' :
                      idx === 2 ? 'bg-orange-50 border border-orange-200' :
                      'bg-white border border-slate-100'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      idx === 0 ? 'bg-amber-400 text-white' :
                      idx === 1 ? 'bg-slate-400 text-white' :
                      idx === 2 ? 'bg-orange-400 text-white' :
                      'bg-slate-200 text-slate-600'
                    }`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{entry.user_name}</p>
                      <p className="text-sm text-slate-500">{entry.chapters_read} chapters read</p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-emerald-600">{entry.progress_percent}%</div>
                      {entry.completed && (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                          <Star className="w-3 h-3" fill="currentColor" /> Complete!
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <div className="text-center">
          <p className="text-slate-600 mb-4">
            Want to participate in our Bible reading challenges?
          </p>
          <Button
            onClick={() => isAuthenticated ? navigate('/dashboard') : navigate('/login')}
            className="bg-coral-500 hover:bg-coral-600 text-white rounded-full px-8 py-6 text-lg shadow-lg"
          >
            {isAuthenticated ? 'Go to My Library' : 'Join Creassion Resources'}
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </main>
    </div>
  );
};

export default ReadathonPage;
